import React, { useState, useEffect } from 'react';
import { Clock, Calendar, ChevronRight, History as HistoryIcon, Trash2, Edit2, Save, X, ChevronDown, ChevronUp } from 'lucide-react';
import Layout from '../components/Layout';
import ConfirmationModal from '../components/ConfirmationModal';
import { getAllWorkouts, deleteWorkoutLog, updateWorkoutSession } from '../utils/api';
import { formatTime } from '../utils/time';

const History = () => {
  const [workouts, setWorkouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSessionId, setEditingSessionId] = useState(null);
  const [editSessionData, setEditSessionData] = useState({ date: '', duration: '' });
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [expandedSessionId, setExpandedSessionId] = useState(null);
  const [sessionLogs, setSessionLogs] = useState({});

  useEffect(() => {
    fetchWorkouts();
  }, []);

  const fetchWorkouts = async () => {
    try {
      const res = await getAllWorkouts();
      setWorkouts(res.data || []);
    } catch (err) {
      console.error('Failed to fetch workouts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSession = (workout) => {
    setEditingSessionId(workout.global_session_id);
    setEditSessionData({
      date: workout.date,
      duration: Math.floor(workout.total_duration / 60).toString()
    });
  };

  const saveSessionEdit = async (globalSessionId) => {
    try {
      await updateWorkoutSession(globalSessionId, {
        date: editSessionData.date,
        total_duration: parseInt(editSessionData.duration) * 60
      });
      setEditingSessionId(null);
      fetchWorkouts();
    } catch (err) {
      console.error('Failed to update session:', err);
    }
  };

  const confirmDeleteSession = (globalSessionId) => {
    setDeleteAction({
      type: 'session',
      id: globalSessionId,
      title: 'Delete Session History?',
      message: 'Are you sure you want to delete this workout session? This action cannot be undone.'
    });
    setShowConfirmDelete(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteAction) return;
    try {
      await deleteWorkoutLog(deleteAction.id, 'session');
      setShowConfirmDelete(false);
      setDeleteAction(null);
      fetchWorkouts();
    } catch (err) {
      console.error('Failed to delete session:', err);
    }
  };

  const toggleExpandSession = async (sessionId) => {
    if (expandedSessionId === sessionId) {
      setExpandedSessionId(null);
      return;
    }
    
    setExpandedSessionId(sessionId);
    
    // Fetch detailed logs if not already fetched
    if (!sessionLogs[sessionId]) {
      try {
        const res = await getAllWorkouts(sessionId);
        setSessionLogs(prev => ({ ...prev, [sessionId]: res.data || [] }));
      } catch (err) {
        console.error('Failed to fetch session logs:', err);
      }
    }
  };

  return (
    <Layout title="Session History" subtitle="Your past workouts">
      <div className="max-w-4xl mx-auto space-y-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-[#A3E6351A] text-[#A3E635] flex items-center justify-center">
              <HistoryIcon size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-black uppercase italic tracking-tighter text-white">All Sessions</h2>
              <p className="text-slate-500 text-xs font-bold uppercase tracking-widest">Complete history of your trainings</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A3E635]"></div>
          </div>
        ) : workouts.length === 0 ? (
          <div className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] p-12 text-center space-y-4">
            <div className="w-16 h-16 bg-[#2C2C2E] rounded-full flex items-center justify-center mx-auto text-slate-500">
              <Clock size={32} />
            </div>
            <p className="text-slate-400 font-medium">No workout sessions recorded yet.</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {workouts.map((workout) => (
              <div 
                key={workout.global_session_id} 
                className="bg-[#1C1C1E] border border-[#2C2C2E] rounded-[32px] overflow-hidden transition-all hover:border-[#A3E63533]"
              >
                <div className="p-6 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 flex-1">
                    <div className="w-12 h-12 rounded-full bg-[#2C2C2E] flex items-center justify-center text-slate-400 shrink-0">
                      <Calendar size={20} />
                    </div>
                    
                    {editingSessionId === workout.global_session_id ? (
                      <div className="flex flex-wrap items-center gap-3 flex-1">
                        <input 
                          type="date"
                          value={editSessionData.date}
                          onChange={(e) => setEditSessionData({ ...editSessionData, date: e.target.value })}
                          className="bg-[#121214] border border-[#2C2C2E] rounded-xl px-3 py-2 text-sm font-bold text-white focus:outline-none focus:border-[#A3E635]"
                        />
                        <div className="flex items-center gap-2">
                          <input 
                            type="number"
                            value={editSessionData.duration}
                            onChange={(e) => setEditSessionData({ ...editSessionData, duration: e.target.value })}
                            className="bg-[#121214] border border-[#2C2C2E] rounded-xl px-3 py-2 text-sm font-bold text-white w-20 focus:outline-none focus:border-[#A3E635]"
                          />
                          <span className="text-xs font-bold text-slate-500 uppercase">min</span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-black uppercase italic tracking-tighter text-white truncate">
                            {workout.routine_name || 'Completed Workout'}
                          </h4>
                          <span className="text-[10px] bg-[#A3E6351A] text-[#A3E635] px-2 py-0.5 rounded-full font-black uppercase tracking-wider">
                            {formatTime(workout.total_duration)}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest mt-1">
                          {new Date(workout.date).toLocaleDateString(undefined, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {editingSessionId === workout.global_session_id ? (
                      <>
                        <button 
                          onClick={() => saveSessionEdit(workout.global_session_id)}
                          className="p-3 bg-[#A3E635] text-black rounded-full hover:scale-105 transition-transform"
                        >
                          <Save size={18} />
                        </button>
                        <button 
                          onClick={() => setEditingSessionId(null)}
                          className="p-3 bg-[#2C2C2E] text-white rounded-full hover:bg-[#3C3C3E] transition-colors"
                        >
                          <X size={18} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button 
                          onClick={() => toggleExpandSession(workout.global_session_id)}
                          className="p-3 text-slate-400 hover:text-white transition-colors"
                          title="View Sequence"
                        >
                          {expandedSessionId === workout.global_session_id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                        </button>
                        <button 
                          onClick={() => handleEditSession(workout)}
                          className="p-3 text-slate-400 hover:text-[#A3E635] transition-colors"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button 
                          onClick={() => confirmDeleteSession(workout.global_session_id)}
                          className="p-3 text-slate-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={18} />
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {expandedSessionId === workout.global_session_id && workout.routine_sequence && (
                  <div className="px-6 pb-6 pt-2 border-t border-[#2C2C2E] bg-black/20">
                    <h5 className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3E635] mb-4">Routine Sequence</h5>
                    <div className="space-y-2">
                      {(() => {
                        const sequence = JSON.parse(workout.routine_sequence);
                        const logs = sessionLogs[workout.global_session_id] || [];
                        const elements = [];
                        let displayIdx = 1;
                        let exerciseLogs = logs.filter(l => l.exercise_id !== null);

                        for (let i = 0; i < sequence.length; i++) {
                          const step = sequence[i];
                          
                          if (step.type === 'block_header' || step.type === 'block') {
                             elements.push(
                               <div key={`block-${i}`} className="pt-4 pb-1">
                                 <div className="flex items-center gap-2">
                                   <div className="h-[1px] flex-1 bg-gradient-to-r from-[#A3E635]/30 to-transparent"></div>
                                   <span className="text-[12px] font-black uppercase tracking-widest text-[#A3E635] italic">
                                     {step.title}
                                   </span>
                                   <div className="h-[1px] flex-[6] bg-gradient-to-r from-[#A3E635]/10 to-transparent"></div>
                                 </div>
                               </div>
                             );
                             continue;
                          }

                          // The "Egg" Model: If this is an exercise, check if next step is its break
                          if (step.type === 'exercise') {
                            // Find all consecutive sets of the same exercise
                            let consecutiveSets = 1;
                            let j = i + 1;
                            while (j < sequence.length) {
                              const nextStep = sequence[j];
                              if (nextStep.type === 'exercise' && nextStep.exerciseId === step.exerciseId) {
                                consecutiveSets++;
                                j++;
                              } else if (nextStep.type === 'break' && nextStep.isAttached) {
                                j++; // Skip attached break
                              } else {
                                break;
                              }
                            }

                            const attachedBreak = sequence.slice(i, j).find(s => s.type === 'break' && s.isAttached);
                            
                            // Find log entries for this exercise in this session
                            const currentExerciseLogs = exerciseLogs.filter(l => l.exercise_id === step.exerciseId);
                            // To be precise, we should probably pick the logs based on their order, 
                            // but since we group them, we'll just show the summary of logs found.
                            // If it's the same exercise multiple times in different parts of the routine, 
                            // this logic might need refinement, but usually they are consecutive sets.
                            
                            let executionSummary = "";
                            if (currentExerciseLogs.length > 0) {
                              const repsList = [];
                              const executionList = [];
                              
                              currentExerciseLogs.forEach(l => {
                                const data = typeof l.data === 'string' ? JSON.parse(l.data) : l.data;
                                if (data) {
                                  if (data.reps) repsList.push(data.reps);
                                  if (data.weight) executionList.push(data.weight + (data.unit || 'kg'));
                                  else if (data.time) executionList.push(formatTime(data.time));
                                }
                              });

                              if (repsList.length > 0) {
                                // notation: [reps]x[execution]
                                // If all reps/execution are the same, simplify? 
                                // The requirement says [reps]x[execution]. 
                                // For multiple sets, it might mean [sets]x[reps]x[execution] or just list them.
                                // "instead of repeating the exercise name as many times as reps it has had"
                                // Wait, "reps" in the requirement might mean "sets".
                                // "The history of exercises should use the notation "[exercise name] - [reps]x[execution]"
                                
                                const sets = currentExerciseLogs.length;
                                const reps = repsList[0];
                                const execution = executionList[0] || "";
                                executionSummary = ` - ${reps}x${execution}`;
                                if (sets > 1) {
                                  executionSummary = ` - ${reps}x${execution} (x${sets})`;
                                }
                              }
                            }

                            elements.push(
                              <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-[#1C1C1E]/50 border border-[#2C2C2E]/50">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black bg-[#2C2C2E] text-slate-400">
                                  {displayIdx++}
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs font-bold text-white leading-tight">
                                    {step.title}{executionSummary} {attachedBreak && <span className="text-slate-500 font-normal ml-1">// Break {attachedBreak.duration}s</span>}
                                  </div>
                                </div>
                              </div>
                            );
                            
                            i = j - 1; // Skip the sets we grouped
                            // Remove used logs so they don't appear in the next group of the same exercise
                            exerciseLogs = exerciseLogs.filter(l => !currentExerciseLogs.includes(l));

                          } else if (step.type === 'break') {
                            // Standalone break or block break
                            elements.push(
                              <div key={i} className="flex items-center gap-3 p-3 rounded-2xl bg-[#1C1C1E]/50 border border-[#2C2C2E]/50">
                                <div className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black bg-blue-900/30 text-blue-400">
                                  <Clock size={12} />
                                </div>
                                <div className="flex-1">
                                  <div className="text-xs font-bold text-white">
                                    Break ({step.duration}s)
                                  </div>
                                </div>
                              </div>
                            );
                          }
                        }
                        return elements;
                      })()}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal 
        isOpen={showConfirmDelete}
        onClose={() => setShowConfirmDelete(false)}
        onConfirm={handleDeleteConfirm}
        title={deleteAction?.title}
        message={deleteAction?.message}
      />
    </Layout>
  );
};

export default History;
